/**
 * Administration → Statistics: storage history, imports and exports, network
 * traffic, and what it costs. The cost and forecast maths are pure functions
 * here so the API, the web app and the tests share one definition.
 *
 * Days are UTC calendar days ('YYYY-MM-DD'), months 'YYYY-MM'. Byte figures shown
 * elsewhere in PRISM use 1 GB = 10^9 bytes (GB below). Costs use the unit of the cost
 * settings (1.2.5): GiB (2^30 bytes, the default, as AWS and OCI bill) or GB (10^9);
 * see unitBytes.
 */
import { z } from 'zod';

export const GB = 1e9;

export const STATISTICS_RANGES = ['30d', '90d', '12m'] as const;
export type StatisticsRange = (typeof STATISTICS_RANGES)[number];

export type TrafficDirection = 'ingress' | 'egress';
/** What moved the bytes. */
/**
 * What moved the bytes. 1.2.5: `processing` is media-worker reads and writes (estimated from file sizes,
 * recorded at the storage location only); `external` is a bucket read by a service outside PRISM (an external
 * transcoder), billed even when the location is on the same network as PRISM.
 */
export const TRAFFIC_KINDS = ['upload', 'watch', 'agent', 'stream', 'download', 'delivery', 'transfer', 'export', 'restore', 'processing', 'external'] as const;
export type TrafficKind = (typeof TRAFFIC_KINDS)[number];
export const TRAFFIC_KIND_LABEL: Record<TrafficKind, string> = {
  upload: 'Uploads', watch: 'Watch folders', agent: 'Storage agents', stream: 'Playback', download: 'Downloads',
  delivery: 'Export deliveries', transfer: 'Transfers', export: 'Exports', restore: 'Restores from archives',
  processing: 'Processing (workers)', external: 'External services',
};

/* ------------------------------------------------------------ work (1.2.5) */

/** What a work_daily row counts. */
export const WORK_METRICS = ['job', 'transcode', 'qc', 'restore', 'signin'] as const;
export type WorkMetric = (typeof WORK_METRICS)[number];

/**
 * Where the work ran: PRISM (media worker / API), a storage agent, an external service, or `unrecorded`
 * (older rows whose venue cannot be inferred). Only transcode and qc rows have one; restore rows carry the
 * archive location's id instead; others are ''.
 */
export const VENUES = ['prism', 'agent', 'external', 'unrecorded'] as const;
export type Venue = (typeof VENUES)[number];

/** metric 'job': the job's kind. */
export const JOB_KINDS = ['ingest', 'transfer', 'export', 'qc', 'workflow', 'subtitles', 'restore', 'migration', 'live'] as const;
export type JobKind = (typeof JOB_KINDS)[number];
/** metric 'job': how it ended (a partial result counts as failed). */
export const JOB_OUTCOMES = ['succeeded', 'failed', 'cancelled'] as const;
export type JobOutcome = (typeof JOB_OUTCOMES)[number];

/** metric 'transcode': what was made. */
export const TRANSCODE_KINDS = ['proxy', 'rendition', 'external', 'export'] as const;
export type TranscodeKind = (typeof TRANSCODE_KINDS)[number];
/** metric 'transcode' and 'restore': how it ended. */
export const TRANSCODE_OUTCOMES = ['succeeded', 'failed'] as const;
export type TranscodeOutcome = (typeof TRANSCODE_OUTCOMES)[number];
export const RESTORE_OUTCOMES = TRANSCODE_OUTCOMES;
export type RestoreOutcome = TranscodeOutcome;

/* metric 'qc': the row's kind is what triggered the run, QC_TRIGGERS / QcTrigger in qc.ts. */
/** metric 'qc': the result. */
export const QC_OUTCOMES = ['passed', 'warnings', 'failed', 'error'] as const;
export type QcOutcome = (typeof QC_OUTCOMES)[number];

/** metric 'restore': the retrieval tier (the row's kind); '' for OCI, which has none. */
export const RESTORE_TIERS = ['Expedited', 'Standard', 'Bulk', ''] as const;
export type RestoreTier = (typeof RESTORE_TIERS)[number];

/** metric 'signin': how (the row's kind) and the result. */
export const SIGNIN_KINDS = ['password', 'sso'] as const;
export type SigninKind = (typeof SIGNIN_KINDS)[number];
export const SIGNIN_OUTCOMES = ['success', 'denied'] as const;
export type SigninOutcome = (typeof SIGNIN_OUTCOMES)[number];

/** Work per bucket and dimensions. Durations in milliseconds; null when nothing was measured. */
export interface WorkRow {
  bucket: string;
  metric: WorkMetric;
  /** JobKind, TranscodeKind, QcTrigger, RestoreTier or SigninKind, by metric. */
  kind: string;
  /** A Venue for transcode and qc; the archive location's id for restore; '' otherwise. */
  venue: string;
  /** JobOutcome, TranscodeOutcome, QcOutcome or SigninOutcome, by metric. */
  outcome: string;
  count: number;
  /** Bytes involved (restores: the bytes restored); 0 when not applicable. */
  bytes: number;
  /** Wait = started − created; run = finished − started. A month's p95 is the count-weighted average of daily p95 values (approximate). */
  waitMsAvg: number | null;
  waitMsP95: number | null;
  runMsAvg: number | null;
  runMsP95: number | null;
}

/** People per bucket: distinct active users (per month for 12m), sign-ins and denied sign-ins. */
export interface UserActivityRow {
  bucket: string;
  activeUsers: number;
  signIns: number;
  signInsDenied: number;
}

/** Where an import came from (the same groups as Monitor → Ingest). */
export const ACTIVITY_KINDS = ['upload', 'watch', 'other', 'export'] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

const Money = z.number().min(0).max(1_000_000);
const Amount = z.number().min(0).max(1e12);
/** The unit costs are calculated in: GiB = 2^30 bytes (AWS, OCI), GB = 10^9 bytes. */
export const COST_UNITS = ['GiB', 'GB'] as const;
export type CostUnit = (typeof COST_UNITS)[number];
export const UNIT_BYTES: Record<CostUnit, number> = { GiB: 2 ** 30, GB: 1e9 };
/** Bytes in one cost unit. */
export const unitBytes = (unit: CostUnit): number => UNIT_BYTES[unit];

/**
 * An entry's prices: storage per unit-month and the units free, and (1.2.5) egress per unit and the units free
 * per month (not billed for an entry on the same network as PRISM), and archive retrieval per unit read.
 */
const LocationPrice = z.object({
  pricePerGbMonth: Money.default(0), freeGb: Amount.default(0),
  egressPerGb: Money.default(0), freeEgressGbPerMonth: Amount.default(0), retrievalPerGb: Money.default(0),
});
export type LocationPrice = z.infer<typeof LocationPrice>;
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
/** A statistics entry id: a storage location's id, or agent:<agentId> for a storage agent. */
export const ENTRY_ID = new RegExp(`^(agent:)?${UUID}$`, 'i');

export const CostSettingsSchema = z.object({
  /** 2 since 1.2.5 (unit, per-entry egress and retrieval); settings without one are v1 (normalizeCostSettings). */
  schemaVersion: z.literal(2).default(2),
  /** ISO 4217 code; amounts are shown with Intl.NumberFormat. */
  currency: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, 'Use a three-letter currency code such as EUR'),
  /** The unit prices are per (1.2.5): GiB by default, as AWS and OCI bill. */
  unit: z.enum(COST_UNITS).default('GiB'),
  /** The PRISM network's own ingress and egress (traffic at PRISM core). */
  ingress: z.object({ pricePerGb: Money.default(0), freeGbPerMonth: Amount.default(0) }).default({ pricePerGb: 0, freeGbPerMonth: 0 }),
  egress: z.object({ pricePerGb: Money.default(0), freeGbPerMonth: Amount.default(0) }).default({ pricePerGb: 0, freeGbPerMonth: 0 }),
  /**
   * Per statistics entry: the price per GB-month and the GB that are free. The key is a storage location's id,
   * or `agent:<agentId>` for all of a storage agent's folders (0.90.1); a location id of an agent's folder
   * is still read (see entryPrices). 1.2.5: also each entry's egress and retrieval prices.
   */
  locations: z.record(z.string().regex(ENTRY_ID, 'A storage location id or agent:<agent id>'), LocationPrice).default({}),
});
export type CostSettings = z.infer<typeof CostSettingsSchema>;

export const DEFAULT_COST_SETTINGS: CostSettings = {
  schemaVersion: 2, currency: 'EUR', unit: 'GiB', ingress: { pricePerGb: 0, freeGbPerMonth: 0 }, egress: { pricePerGb: 0, freeGbPerMonth: 0 }, locations: {},
};

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Stored settings with anything missing or invalid replaced by the defaults. v1 settings (no schemaVersion,
 * before 1.2.5) had one egress price for every endpoint: it is copied to each entry's egressPerGb so existing
 * totals stay close, and the unit becomes GiB.
 */
export function normalizeCostSettings(value: unknown): CostSettings {
  let raw: Record<string, unknown> = isObject(value) ? value : {};
  if (raw.schemaVersion === undefined) {
    const egress = isObject(raw.egress) && typeof raw.egress.pricePerGb === 'number' ? raw.egress.pricePerGb : 0;
    const locations = isObject(raw.locations)
      ? Object.fromEntries(Object.entries(raw.locations).map(([id, p]) => [id, isObject(p) ? { egressPerGb: egress, ...p } : p]))
      : raw.locations;
    raw = { ...raw, schemaVersion: 2, unit: 'GiB', ...(locations === undefined ? {} : { locations }) };
  }
  const r = CostSettingsSchema.safeParse({ ...DEFAULT_COST_SETTINGS, ...raw });
  return r.success ? r.data : DEFAULT_COST_SETTINGS;
}

/** Whether a currency code is one Intl can format. */
export function isCurrency(code: string): boolean {
  try {
    new Intl.NumberFormat('en', { style: 'currency', currency: code }).format(1);
    return /^[A-Z]{3}$/.test(code);
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------ calendar (UTC) */

const parseDay = (day: string) => Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
const fmtDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export const dayOf = (d: Date): string => d.toISOString().slice(0, 10);
export const addDays = (day: string, n: number): string => fmtDay(parseDay(day) + n * 86_400_000);
export const daysBetween = (from: string, to: string): number => Math.round((parseDay(to) - parseDay(from)) / 86_400_000);
export const monthOf = (day: string): string => day.slice(0, 7);
/** The first day of the UTC month a moment is in (1.6.1): the month a provider bills it in, whatever the server's zone. */
export const billingMonthStart = (d: Date): string => `${monthOf(dayOf(d))}-01`;
/** How much of its UTC day a moment has passed, 0–1 (1.6.1): 24 hours a day, also on a clock-change day. */
export const utcDayFraction = (d: Date): number => (d.getTime() - Date.parse(`${dayOf(d)}T00:00:00Z`)) / 86_400_000;
export function daysInMonth(month: string): number {
  return new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).getUTCDate();
}
export function addMonths(month: string, n: number): string {
  const d = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1 + n, 1));
  return d.toISOString().slice(0, 7);
}
/** Every day from `from` to `to`, both included. */
export function eachDay(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}
export function eachMonth(from: string, to: string): string[] {
  const out: string[] = [];
  for (let m = from; m <= to; m = addMonths(m, 1)) out.push(m);
  return out;
}
/** The first month of the quarter a month is in. */
export const quarterStart = (month: string): string => `${month.slice(0, 4)}-${String(Math.floor((Number(month.slice(5, 7)) - 1) / 3) * 3 + 1).padStart(2, '0')}`;

/** The first day of a range ending today, and how its points are bucketed. */
export function rangeWindow(range: StatisticsRange, today: string): { from: string; bucket: 'day' | 'month' } {
  if (range === '30d') return { from: addDays(today, -29), bucket: 'day' };
  if (range === '90d') return { from: addDays(today, -89), bucket: 'day' };
  return { from: `${addMonths(monthOf(today), -11)}-01`, bucket: 'month' };
}

/* ------------------------------------------------------------ storage series */

/**
 * Daily values from snapshots, carrying the last snapshot forward over days
 * without one (a day the API did not run). Days before the first snapshot
 * have no value: history starts when statistics were deployed.
 */
export function fillDaily(points: Array<{ day: string; bytes: number }>, from: string, to: string): Map<string, number> {
  const sorted = [...points].sort((a, b) => a.day.localeCompare(b.day));
  const out = new Map<string, number>();
  let i = 0;
  let last: number | null = null;
  while (i < sorted.length && sorted[i]!.day < from) last = sorted[i++]!.bytes;
  for (const day of eachDay(from, to)) {
    while (i < sorted.length && sorted[i]!.day === day) last = sorted[i++]!.bytes;
    if (last !== null) out.set(day, last);
  }
  return out;
}

/** Least squares line through (x, y); a single point gives a flat line. */
export function linearTrend(points: Array<[number, number]>): { slope: number; intercept: number } {
  if (!points.length) return { slope: 0, intercept: 0 };
  if (points.length === 1) return { slope: 0, intercept: points[0]![1] };
  const n = points.length;
  const mx = points.reduce((s, p) => s + p[0], 0) / n;
  const my = points.reduce((s, p) => s + p[1], 0) / n;
  let num = 0;
  let den = 0;
  for (const [x, y] of points) {
    num += (x - mx) * (y - my);
    den += (x - mx) ** 2;
  }
  const slope = den === 0 ? 0 : num / den;
  return { slope, intercept: my - slope * mx };
}

/* ------------------------------------------------------------ costs */

/** One day's share of a month's storage bill: units above the free amount × price per unit-month ÷ days in the month. */
export function storageDayCost(bytes: number, day: string, pricePerGbMonth: number, freeGb: number, unit: number = GB): number {
  return (Math.max(0, bytes / unit - freeGb) * pricePerGbMonth) / daysInMonth(monthOf(day));
}

/** A month's traffic bill: units above the monthly free amount × price per unit. */
export function trafficCost(bytes: number, pricePerGb: number, freeGbPerMonth: number, unit: number = GB): number {
  return Math.max(0, bytes / unit - freeGbPerMonth) * pricePerGb;
}

/**
 * The archive early-deletion fee of one copy removed from a cold location (1.2.5): the days left of the archive's
 * minimum storage duration, billed at the storage price as a share of the removal month. 0 without a minimum, or
 * when the copy was kept long enough.
 */
export function earlyDeletionFee(r: { day: string; bytes: number; storedDays: number; minimumDays: number | null }, pricePerGbMonth: number, unit: number = GB): number {
  if (!r.minimumDays) return 0;
  const remaining = r.minimumDays - Math.max(0, r.storedDays);
  if (!(remaining > 0)) return 0;
  return ((r.bytes / unit) * pricePerGbMonth * remaining) / daysInMonth(monthOf(r.day));
}

export interface MonthCost {
  month: string;
  storage: number;
  /** All ingress: prismIngress (location ingress is free). */
  ingress: number;
  /** All egress: prismEgress + storageEgress. */
  egress: number;
  /** Traffic at PRISM core, at the PRISM network's prices (1.2.5). */
  prismIngress: number;
  prismEgress: number;
  /** Egress at entries not on the same network as PRISM (and `external` reads everywhere), at each entry's price. */
  storageEgress: number;
  /** Archive retrieval: bytes read from cold entries × their retrieval price. */
  retrieval: number;
  /** Archive early-deletion fees: copies removed before the archive's minimum storage days. */
  earlyDeletion: number;
  total: number;
  /** What has been spent (to today). */
  actual: number;
  /** The forecast part: the rest of the current month, and later months. */
  forecast: number;
  /** Units (settings.unit) moved in the month, billable endpoints only (projected for the current and later months). */
  ingressGb: number;
  egressGb: number;
}

export interface CostInput {
  today: string;
  settings: CostSettings;
  /** Daily storage snapshots per entry. */
  storage: Array<{ locationId: string; day: string; bytes: number }>;
  /** Daily traffic bytes per entry; location null is PRISM core. `kind` 'external' is billed even on the same network. */
  traffic: Array<{ day: string; locationId: string | null; direction: TrafficDirection; kind?: string; bytes: number }>;
  /** Entries on the same network as PRISM: their egress is internal and never billed (but retrieval and `external` reads are). */
  internalLocations: Iterable<string>;
  /** Archive (cold) entries: every byte read from them is billed at their retrieval price (1.2.5). */
  coldLocations?: Iterable<string>;
  /** Copies removed from archive entries (cold_removals), with the archive's minimum storage days (1.2.5). */
  removals?: Array<{ locationId: string; day: string; bytes: number; storedDays: number; minimumDays: number | null }>;
  /**
   * How much of today has passed (0–1, default 1): the current month's traffic is projected from the month-to-date
   * daily average over the days elapsed, today counted by this fraction (at least an hour).
   */
  todayFraction?: number;
  /** The trend window for storage forecasts. */
  trendDays?: number;
}

/**
 * The linear trend of an entry's daily values over the `trendDays` days to today; null without a value today. The
 * storage forecast and the cost forecast both use it, so the storage curve and the forecast costs agree.
 */
export function storageTrend(filled: Map<string, number>, today: string, trendDays = 30): { slope: number; intercept: number } | null {
  if (!filled.has(today)) return null;
  const window: Array<[number, number]> = [];
  for (let i = trendDays - 1; i >= 0; i--) {
    const v = filled.get(addDays(today, -i));
    if (v !== undefined) window.push([-i, v]);
  }
  return linearTrend(window);
}

/** Bytes on a day after today along a trend, never below zero. */
export const trendAt = (t: { slope: number; intercept: number }, today: string, day: string): number => Math.max(0, t.intercept + t.slope * daysBetween(today, day));

/**
 * Cost per month from `fromMonth` to `toMonth`, with a forecast for the days after today (1.2.5 cost model).
 *
 * - Storage: each day costs its share of the month (storageDayCost) per entry. After today each entry follows the
 *   linear trend of its last 30 days (storageTrend, never below zero).
 * - PRISM network: traffic at PRISM core (location null) at the settings' ingress and egress prices.
 * - Storage egress: per entry not on the same network, its egress × its egressPerGb above freeEgressGbPerMonth.
 *   Kind `external` (a service outside PRISM reading the bucket) is billed even on the same network. Ingress at a
 *   location is free.
 * - Retrieval: every byte read from an archive entry × its retrievalPerGb, also on the same network.
 * - Early deletion: per copy removed from an archive before its minimum storage days (earlyDeletionFee).
 * - The current month's traffic is projected from its month-to-date daily average (the part of today that has
 *   passed counted as a fraction); later months use that same average. Retrieval and early deletion are actuals only.
 *
 * Every amount is in the settings' unit (GiB or GB, unitBytes).
 */
export function monthlyCosts(input: CostInput, fromMonth: string, toMonth: string): MonthCost[] {
  const { today, settings } = input;
  const unit = unitBytes(settings.unit ?? 'GiB');
  const trendDays = input.trendDays ?? 30;
  const internal = new Set(input.internalLocations);
  const cold = new Set(input.coldLocations ?? []);
  const firstDay = `${fromMonth}-01`;
  const lastDay = `${toMonth}-${String(daysInMonth(toMonth)).padStart(2, '0')}`;
  const priceOf = (id: string): LocationPrice => settings.locations[id] ?? { pricePerGbMonth: 0, freeGb: 0, egressPerGb: 0, freeEgressGbPerMonth: 0, retrievalPerGb: 0 };
  const add = (m: Map<string, number>, k: string, v: number) => m.set(k, (m.get(k) ?? 0) + v);

  // Storage per entry: actual daily values to today, the trend after.
  const byLocation = new Map<string, Array<{ day: string; bytes: number }>>();
  for (const s of input.storage) byLocation.set(s.locationId, [...(byLocation.get(s.locationId) ?? []), { day: s.day, bytes: s.bytes }]);
  const storageActual = new Map<string, number>(); // month → cost to today
  const storageForecast = new Map<string, number>(); // month → cost after today
  for (const [locationId, points] of byLocation) {
    const price = priceOf(locationId);
    const earliest = points.reduce((a, p) => (p.day < a ? p.day : a), points[0]!.day);
    const filled = fillDaily(points, earliest < firstDay ? earliest : firstDay, today);
    for (const [day, bytes] of filled) {
      if (day < firstDay || day > lastDay) continue;
      add(storageActual, monthOf(day), storageDayCost(bytes, day, price.pricePerGbMonth, price.freeGb, unit));
    }
    if (lastDay <= today) continue;
    const trend = storageTrend(filled, today, trendDays);
    if (!trend) continue;
    for (const day of eachDay(addDays(today, 1) > firstDay ? addDays(today, 1) : firstDay, lastDay)) {
      add(storageForecast, monthOf(day), storageDayCost(trendAt(trend, today, day), day, price.pricePerGbMonth, price.freeGb, unit));
    }
  }

  // Billable bytes per bill and month: 'core:ingress', 'core:egress' and 'egress:<entry>' (storage egress); retrieval per month.
  const bills = new Map<string, Map<string, number>>();
  const bill = (key: string) => bills.get(key) ?? bills.set(key, new Map()).get(key)!;
  const retrievalCost = new Map<string, number>();
  for (const t of input.traffic) {
    if (t.day > today) continue;
    const month = monthOf(t.day);
    if (!t.locationId) {
      add(bill(`core:${t.direction}`), month, t.bytes);
      continue;
    }
    if (t.direction !== 'egress') continue; // ingress at a location is free
    if (cold.has(t.locationId)) add(retrievalCost, month, (t.bytes / unit) * priceOf(t.locationId).retrievalPerGb);
    if (internal.has(t.locationId) && t.kind !== 'external') continue;
    add(bill(`egress:${t.locationId}`), month, t.bytes);
  }
  const earlyCost = new Map<string, number>();
  for (const r of input.removals ?? []) {
    if (r.day > today) continue;
    add(earlyCost, monthOf(r.day), earlyDeletionFee(r, priceOf(r.locationId).pricePerGbMonth, unit));
  }
  const priced = (key: string): { pricePerGb: number; freeGbPerMonth: number } => {
    if (key === 'core:ingress') return settings.ingress;
    if (key === 'core:egress') return settings.egress;
    const p = priceOf(key.slice('egress:'.length));
    return { pricePerGb: p.egressPerGb, freeGbPerMonth: p.freeEgressGbPerMonth };
  };

  const thisMonth = monthOf(today);
  // Days of this month that have passed: the whole days before today and the part of today (1.2.5; before, today
  // counted as a whole day, so the forecast was low early in the day).
  const elapsed = Number(today.slice(8, 10)) - 1 + Math.min(1, Math.max(1 / 24, input.todayFraction ?? 1));

  return eachMonth(fromMonth, toMonth).map((month) => {
    const days = daysInMonth(month);
    let prismIngress = 0, prismEgress = 0, storageEgress = 0, trafficActual = 0, ingressBytes = 0, egressBytes = 0;
    for (const [key, byMonth] of bills) {
      const actual = month > thisMonth ? 0 : byMonth.get(month) ?? 0;
      const average = (byMonth.get(thisMonth) ?? 0) / elapsed;
      const projected = month < thisMonth ? actual : month === thisMonth ? actual + average * (days - elapsed) : average * days;
      const { pricePerGb, freeGbPerMonth } = priced(key);
      const cost = trafficCost(projected, pricePerGb, freeGbPerMonth, unit);
      if (month <= thisMonth) trafficActual += trafficCost(actual, pricePerGb, freeGbPerMonth, unit);
      if (key === 'core:ingress') { prismIngress += cost; ingressBytes += projected; }
      else if (key === 'core:egress') { prismEgress += cost; egressBytes += projected; }
      else { storageEgress += cost; egressBytes += projected; }
    }
    const retrieval = month > thisMonth ? 0 : retrievalCost.get(month) ?? 0;
    const earlyDeletion = month > thisMonth ? 0 : earlyCost.get(month) ?? 0;
    const storageActualCost = storageActual.get(month) ?? 0;
    const storage = storageActualCost + (storageForecast.get(month) ?? 0);
    const total = storage + prismIngress + prismEgress + storageEgress + retrieval + earlyDeletion;
    const actual = month > thisMonth ? 0 : storageActualCost + trafficActual + retrieval + earlyDeletion;
    return {
      month, storage, ingress: prismIngress, egress: prismEgress + storageEgress, prismIngress, prismEgress, storageEgress, retrieval, earlyDeletion,
      total, actual, forecast: Math.max(0, total - actual), ingressGb: ingressBytes / unit, egressGb: egressBytes / unit,
    };
  });
}

/**
 * The storage forecast per entry (1.2.5): for 'day' buckets each of the `days` days after today, for 'month' buckets
 * the value at the end of each of the next `months` months; each entry along storageTrend (never below zero), so the
 * curve agrees with the cost forecast. Entries without a value today are left out.
 */
export function storageForecastSeries(
  storage: Array<{ locationId: string; day: string; bytes: number }>, today: string,
  horizon: { bucket: 'day'; days: number } | { bucket: 'month'; months: number }, trendDays = 30,
): Array<{ bucket: string; values: Record<string, number> }> {
  const byEntry = new Map<string, Array<{ day: string; bytes: number }>>();
  for (const s of storage) byEntry.set(s.locationId, [...(byEntry.get(s.locationId) ?? []), { day: s.day, bytes: s.bytes }]);
  const trends = new Map<string, { slope: number; intercept: number }>();
  for (const [id, points] of byEntry) {
    const t = storageTrend(fillDaily(points, addDays(today, -(trendDays - 1)), today), today, trendDays);
    if (t) trends.set(id, t);
  }
  const points: Array<{ bucket: string; day: string }> = horizon.bucket === 'day'
    ? eachDay(addDays(today, 1), addDays(today, horizon.days)).map((day) => ({ bucket: day, day }))
    : eachMonth(addMonths(monthOf(today), 1), addMonths(monthOf(today), horizon.months)).map((m) => ({ bucket: m, day: `${m}-${String(daysInMonth(m)).padStart(2, '0')}` }));
  return points.map(({ bucket, day }) => ({ bucket, values: Object.fromEntries([...trends].map(([id, t]) => [id, Math.round(trendAt(t, today, day))])) }));
}

/* ------------------------------------------------------------ work buckets (1.2.5) */

/**
 * Daily work rows (work_daily) added up per bucket and dimensions: counts and bytes are summed; averages are
 * count-weighted, and so is a month's p95 (the count-weighted average of the daily p95 values: approximate). A
 * duration with no daily value stays null.
 */
export function bucketWorkRows(rows: Array<Omit<WorkRow, 'bucket'> & { day: string }>, bucketOf: (day: string) => string): WorkRow[] {
  type Acc = WorkRow & { w: Record<'waitMsAvg' | 'waitMsP95' | 'runMsAvg' | 'runMsP95', [number, number]> };
  const DUR = ['waitMsAvg', 'waitMsP95', 'runMsAvg', 'runMsP95'] as const;
  const out = new Map<string, Acc>();
  for (const r of rows) {
    const bucket = bucketOf(r.day);
    const key = `${bucket}|${r.metric}|${r.kind}|${r.venue}|${r.outcome}`;
    let a = out.get(key);
    if (!a) {
      a = { bucket, metric: r.metric, kind: r.kind, venue: r.venue, outcome: r.outcome, count: 0, bytes: 0, waitMsAvg: null, waitMsP95: null, runMsAvg: null, runMsP95: null, w: { waitMsAvg: [0, 0], waitMsP95: [0, 0], runMsAvg: [0, 0], runMsP95: [0, 0] } };
      out.set(key, a);
    }
    a.count += r.count;
    a.bytes += r.bytes;
    for (const d of DUR) {
      const v = r[d];
      if (v === null || v === undefined || !(r.count > 0)) continue;
      a.w[d][0] += v * r.count;
      a.w[d][1] += r.count;
    }
  }
  return [...out.values()].map(({ w, ...a }) => {
    for (const d of DUR) a[d] = w[d][1] > 0 ? Math.round(w[d][0] / w[d][1]) : null;
    return a;
  });
}

/** Forecast totals for the current month, quarter and year (spent so far plus the forecast). */
export function costForecast(months: MonthCost[], today: string): { month: number; quarter: number; year: number } {
  const m = monthOf(today);
  const q = quarterStart(m);
  const qEnd = addMonths(q, 2);
  const y = m.slice(0, 4);
  const sum = (pred: (month: string) => boolean) => months.filter((x) => pred(x.month)).reduce((s, x) => s + x.total, 0);
  return { month: sum((x) => x === m), quarter: sum((x) => x >= q && x <= qEnd), year: sum((x) => x.startsWith(y)) };
}

/* ------------------------------------------------------------ API shape */

/** The statistics entry of a storage agent: all its folders (storage, export, watch) count as one place, on one site. */
export const agentEntryId = (agentId: string): string => `agent:${agentId}`;

/**
 * One place in the statistics: a storage location, or a storage agent with all its folders (0.90.1). Every
 * chart, table and cost uses entries, so a site's storage and export folders are counted once.
 */
export interface StatisticsLocation {
  /** The location's id, or agentEntryId(agentId) for a storage agent. */
  id: string;
  /** The location's name, or the storage agent's name. */
  name: string;
  kind: 'fs' | 's3' | 'oci' | 'agent';
  /** 1.2.5: which combined storage group it is in: archive (cold), on-site (server folders and storage agents), or cloud. */
  storageGroup: StorageGroup;
  /** 'export' or 'edit' (1.0.0-rc.6) only for a single export or edit location; a storage agent is 'storage'. */
  purpose: 'storage' | 'export' | 'edit';
  /**
   * Every folder is on the same network as PRISM: only then is the entry's traffic internal. A storage agent's watch and
   * edit folders (in its entry since 1.2.5) count only when it has no storage or export folder.
   */
  sameNetwork: boolean;
  /** Some of a storage agent's folders are on PRISM's network and some are not (counted as not). */
  networkMixed: boolean;
  /** Every location of the entry was removed. */
  removed: boolean;
  /** The storage locations it covers: one, or the agent's folders (storage first). */
  locationIds: string[];
  /** For a storage agent: its id and what its folders are for. */
  agentId: string | null;
  purposes: Array<'storage' | 'export' | 'ingest' | 'edit'>;
  /**
   * Export folders are measured, not taken from records (1.0.0-rc.13 second round): null for an entry without one.
   * `estimated`: not measured yet (an agent before rc.13, or before the first measurement): the recorded exports;
   * `atLeast`: a walk stopped at its file cap; `current`: false when the last attempt failed or the agent was offline
   * (the figures are from `measuredAt`).
   */
  measurement?: { estimated: boolean; atLeast: boolean; current: boolean; measuredAt: string | null; error: string | null } | null;
}

export const STORAGE_GROUPS = ['cloud', 'archive', 'on-site'] as const;
export type StorageGroup = (typeof STORAGE_GROUPS)[number];
/** A location's storage group: cold → archive, server folder or agent → on-site, otherwise cloud. */
export const storageGroupOf = (kind: StatisticsLocation['kind'], cold: boolean): StorageGroup =>
  cold ? 'archive' : kind === 'fs' || kind === 'agent' ? 'on-site' : 'cloud';

/** A storage location as the statistics read it, before grouping. */
export interface StatisticsLocationRow {
  id: string;
  name: string;
  kind: StatisticsLocation['kind'];
  /** An archive (cold) location (1.2.5; missing counts as not). */
  cold?: boolean;
  purpose: 'storage' | 'export' | 'ingest' | 'edit';
  sameNetwork: boolean;
  removed: boolean;
  agentId: string | null;
  agentName: string | null;
}

const PURPOSE_ORDER = { storage: 0, export: 1, ingest: 2, edit: 3 } as const;
const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'en');

/**
 * Groups locations into statistics entries: a storage agent's folders become one entry named after the
 * agent; every other location is an entry of its own. Locations first, then agents, removed ones last.
 */
export function groupStatisticsLocations(rows: StatisticsLocationRow[]): StatisticsLocation[] {
  const out: StatisticsLocation[] = [];
  const agents = new Map<string, StatisticsLocationRow[]>();
  for (const r of rows) {
    if (r.kind === 'agent' && r.agentId) agents.set(r.agentId, [...(agents.get(r.agentId) ?? []), r]);
    else if (r.purpose !== 'ingest') {
      out.push({
        id: r.id, name: r.name, kind: r.kind, storageGroup: storageGroupOf(r.kind, r.cold ?? false), purpose: r.purpose, sameNetwork: r.sameNetwork, networkMixed: false, removed: r.removed,
        locationIds: [r.id], agentId: null, purposes: [r.purpose],
      });
    }
  }
  for (const [agentId, members] of agents) {
    members.sort((a, b) => PURPOSE_ORDER[a.purpose] - PURPOSE_ORDER[b.purpose] || byName(a, b));
    const live = members.filter((m) => !m.removed);
    const counted = live.length ? live : members;
    // Same network from its storage and export folders when it has any (1.2.5: its watch and edit folders joined the
    // entry; their own flag, never set before, does not change an entry that was on the same network), else from all.
    const placed = counted.filter((m) => m.purpose === 'storage' || m.purpose === 'export');
    const basis = placed.length ? placed : counted;
    const on = basis.filter((m) => m.sameNetwork).length;
    out.push({
      id: agentEntryId(agentId), name: members[0]!.agentName ?? members[0]!.name, kind: 'agent', storageGroup: 'on-site', purpose: 'storage',
      sameNetwork: on === basis.length, networkMixed: on > 0 && on < basis.length, removed: !live.length,
      locationIds: members.map((m) => m.id), agentId, purposes: [...new Set(members.map((m) => m.purpose))],
    });
  }
  return out.sort((a, b) => Number(a.removed) - Number(b.removed) || Number(a.kind === 'agent') - Number(b.kind === 'agent') || byName(a, b));
}

/** Location id → entry id. */
export function entryIndex(entries: StatisticsLocation[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const e of entries) for (const id of e.locationIds) m.set(id, e.id);
  return m;
}

/**
 * Storage snapshots per entry: on each day any of an entry's locations has a snapshot, the sum of every
 * location's latest value (carried forward, as fillDaily does). Locations outside the index keep their id.
 */
export function groupStorageRows(rows: Array<{ locationId: string; day: string; bytes: number }>, index: Map<string, string>): Array<{ locationId: string; day: string; bytes: number }> {
  const byEntry = new Map<string, Array<{ locationId: string; day: string; bytes: number }>>();
  for (const r of rows) {
    const e = index.get(r.locationId) ?? r.locationId;
    byEntry.set(e, [...(byEntry.get(e) ?? []), r]);
  }
  const out: Array<{ locationId: string; day: string; bytes: number }> = [];
  for (const [entry, list] of byEntry) {
    const sorted = [...list].sort((a, b) => a.day.localeCompare(b.day));
    const latest = new Map<string, number>();
    for (let i = 0; i < sorted.length; i++) {
      latest.set(sorted[i]!.locationId, sorted[i]!.bytes);
      if (sorted[i + 1]?.day === sorted[i]!.day) continue;
      out.push({ locationId: entry, day: sorted[i]!.day, bytes: [...latest.values()].reduce((a, b) => a + b, 0) });
    }
  }
  return out;
}

/** Traffic per entry: the rows of an entry's locations added up per day, direction and kind (null stays PRISM core). */
export function groupTrafficRows<T extends { day: string; locationId: string | null; direction: TrafficDirection; kind: string; bytes: number }>(rows: T[], index: Map<string, string>): T[] {
  const out = new Map<string, T>();
  for (const r of rows) {
    const locationId = r.locationId ? index.get(r.locationId) ?? r.locationId : null;
    const key = `${r.day}|${locationId ?? ''}|${r.direction}|${r.kind}`;
    const cur = out.get(key);
    if (cur) cur.bytes += r.bytes;
    else out.set(key, { ...r, locationId });
  }
  return [...out.values()];
}

/**
 * Prices per entry. An entry's own key wins. A storage agent without one takes its folders' prices from
 * before 0.90.1, when each folder had its own: the folder with a price set (above zero, or free GB) if
 * there is one, and with several the highest price per GB-month (a storage folder first when equal),
 * together with that folder's free GB. Keys of locations that are not in an entry are left out.
 */
export function entryPrices<P extends Pick<LocationPrice, 'pricePerGbMonth' | 'freeGb'>>(prices: Record<string, P>, entries: StatisticsLocation[]): Record<string, P> {
  const out: Record<string, P> = {};
  for (const e of entries) {
    const own = prices[e.id];
    if (own) {
      out[e.id] = own;
      continue;
    }
    const set = e.locationIds.map((id) => prices[id]).filter((p): p is P => Boolean(p && (p.pricePerGbMonth > 0 || p.freeGb > 0)));
    if (!set.length) continue;
    out[e.id] = set.reduce((best, p) => (p.pricePerGbMonth > best.pricePerGbMonth ? p : best));
  }
  return out;
}

export interface StatisticsDTO {
  range: StatisticsRange;
  bucket: 'day' | 'month';
  from: string;
  today: string;
  /** Bucket keys in order: days ('YYYY-MM-DD') or months ('YYYY-MM'). */
  buckets: string[];
  /** The first day with a storage snapshot and with traffic (history starts with 0.28.0). */
  trackedSince: {
    storage: string | null;
    traffic: string | null;
    /** The first day with work statistics (1.2.5; from the audit backfill). */
    work: string | null;
    /** The first day where transcode and QC venues were recorded (before it: inferred or unrecorded). */
    venue: string | null;
  };
  currency: string;
  locations: StatisticsLocation[];
  /** Bytes per location per bucket (a month is its daily average); null before tracking started. */
  storage: Array<{ bucket: string; values: Record<string, number | null> }>;
  /**
   * The storage forecast per entry (bytes) for buckets after today: the next 30 days (30d), 90 days (90d) or
   * 12 month-ends (12m); each entry's linear trend of its last 30 days, never below zero (as the cost forecast).
   */
  storageForecast: Array<{ bucket: string; values: Record<string, number> }>;
  /** Imports by source and exports, count and bytes per bucket. */
  activity: Array<{ bucket: string } & Record<ActivityKind, { count: number; bytes: number }>>;
  /** Traffic per bucket, endpoint (null: PRISM core), direction and kind. */
  traffic: Array<{ bucket: string; locationId: string | null; direction: TrafficDirection; kind: string; bytes: number }>;
  costs: { months: MonthCost[]; forecast: { month: number; quarter: number; year: number }; configured: boolean; unit: CostUnit };
  /** Work per bucket (1.2.5): jobs, transcodes, QC, restores and sign-ins. */
  work: WorkRow[];
  /** People per bucket (1.2.5). */
  users: UserActivityRow[];
  /**
   * How the last work rollup went (1.2.5): when every source was last written, and the last run's failure (the first
   * failing source), null when it went through. Absent before the first rollup.
   */
  workRollup?: { lastRunAt: string; lastSuccessAt: string | null; lastError: { source: string; message: string; at: string } | null } | null;
  /** 1.6.0: workflow calls per webhook endpoint per month (webhook_endpoint_usage), for the months of the range. */
  webhookCalls?: Array<{ month: string; endpointId: string; endpoint: string; calls: number; failures: number }>;
}

export interface CostSettingsDTO {
  settings: CostSettings;
  locations: StatisticsLocation[];
}
